import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Estate } from '@/features/estates/api/estates';
import type { MutationConfig } from '@/lib/react-query';
import { api, extractData } from '@/lib/treaty';

export type EstateAnalysis = NonNullable<
  Awaited<ReturnType<ReturnType<ReturnType<typeof api.estates>['analysis']>['get']>>['data']
>;

type EstateAnalysisOptions = {
  id: Estate['id'];
  queryOptions?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof getEstateAnalysis>>,
      Error,
      Awaited<ReturnType<typeof getEstateAnalysis>>,
      ReturnType<typeof getEstateAnalysisOptions>['queryKey']
    >,
    'queryKey' | 'queryFn'
  >;
};

type GetDossierInfoValidOptions = {
  id: Estate['id'];
  queryOptions?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof getEstateAnalysisId>>,
      Error,
      Awaited<ReturnType<typeof getEstateAnalysisId>>,
      ReturnType<typeof getDossierInfoValidOptions>['queryKey']
    >,
    'queryKey' | 'queryFn'
  >;
};

export const getEstateAnalysis = async (id: Estate['id']) => {
  const { analyses } = await getEstateAnalysisId(id);
  let analysisId: string | undefined;

  if (analyses?.length) {
    analysisId = analyses[0].id;
  } else {
    const createEstateAnalysisResponse = await createEstateAnalysis(id);
    analysisId = createEstateAnalysisResponse.analysisId;
  }

  if (!analysisId) {
    throw new Error('Failed to retrieve or create estate analysis');
  }

  const analysis = await extractData(api.estates({ id: id }).analysis({ analysisId }).get());

  return { analysis };
};

export const getEstateAnalysisOptions = (id: Estate['id']) => ({
  queryKey: ['ESTATE_ANALYSIS', id],
  queryFn: () => getEstateAnalysis(id),
});

export const useEstateAnalysis = ({ queryOptions, id }: EstateAnalysisOptions) => {
  return useQuery({ ...queryOptions, ...getEstateAnalysisOptions(id) });
};

// Unlock Analysis -> Create Dossier

type UnlockEstateAnalysisOptions = {
  mutationConfig?: MutationConfig<typeof unlockEstateAnalysis>;
};

export const unlockEstateAnalysis = async (id: Estate['id']) => {
  const response = await extractData(api.estates({ id })['unlock-analysis'].put());
  if (!response.dossierId) {
    throw new Error('Failed to unlock estate analysis');
  }
  return response;
};

export const useUnlockEstateAnalysis = ({ mutationConfig }: UnlockEstateAnalysisOptions) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: unlockEstateAnalysis,
  });
};

// Create Estate Analysis
type CreateEstateAnalysisOptions = {
  mutationConfig?: MutationConfig<typeof createEstateAnalysis>;
};

export const createEstateAnalysis = (id: Estate['id']) => {
  return extractData(api.estates({ id }).analysis.post());
};

export const useCreateEstateAnalysis = ({ mutationConfig }: CreateEstateAnalysisOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: createEstateAnalysis,
  });
};

// Get Estate Analysis
export const getEstateAnalysisId = (id: Estate['id']) => {
  return extractData(api.estates({ id }).analysis.get());
};

export const getDossierInfoValidOptions = (id: Estate['id']) => ({
  queryKey: ['DOSSIER_INFO_VALID', id],
  queryFn: () => getEstateAnalysisId(id),
});

export const useDossierInfoValid = ({ queryOptions, id }: GetDossierInfoValidOptions) => {
  return useQuery({ ...queryOptions, ...getDossierInfoValidOptions(id) });
};
